import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Star } from "lucide-react";
import { api } from "@/api";

const inputCls =
    "w-full px-3 py-2.5 rounded-lg bg-white border border-[#E6DBCF] text-sm text-[#241D19] focus:outline-none focus:ring-2 focus:ring-[#C85A17]/40";

const EMPTY = { name: "", location: "", rating: 5, package_name: "", text: "" };

export const ReviewsManager = () => {
    const [reviews, setReviews] = useState([]);
    const [form, setForm] = useState(null);

    const load = () =>
        api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => toast.error("Could not load reviews"));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!form.name.trim() || !form.text.trim()) {
            toast.error("Customer name and review text are required");
            return;
        }
        try {
            const payload = { ...form, rating: Number(form.rating) };
            if (form.id) await api.put(`/admin/reviews/${form.id}`, payload);
            else await api.post("/admin/reviews", payload);
            toast.success("Review saved");
            setForm(null);
            load();
        } catch {
            toast.error("Could not save review");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this review permanently?")) return;
        try {
            await api.delete(`/admin/reviews/${id}`);
            setReviews((rs) => rs.filter((r) => r.id !== id));
            toast.success("Review deleted");
        } catch {
            toast.error("Could not delete review");
        }
    };

    return (
        <div data-testid="admin-reviews-manager">
            <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-[#6E6259]">{reviews.length} review{reviews.length === 1 ? "" : "s"} shown on website</p>
                <button
                    data-testid="admin-review-new-button"
                    onClick={() => setForm({ ...EMPTY })}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#C85A17] text-white text-xs font-semibold hover:bg-[#9C370B] transition-colors"
                >
                    <Plus size={14} /> Add Review
                </button>
            </div>

            {form && (
                <div data-testid="admin-review-editor" className="bg-white border border-[#E5C158] rounded-2xl p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-serif-alt font-semibold text-xl text-[#241D19]">
                            {form.id ? "Edit Review" : "Add Review"}
                        </h3>
                        <button onClick={() => setForm(null)} className="p-2 rounded-full hover:bg-[#F4EDE2] text-[#6E6259]" aria-label="Close editor">
                            <X size={16} />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input className={inputCls} placeholder="Customer name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-review-name-input" />
                        <input className={inputCls} placeholder="City (e.g. Patna, Bihar)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                        <select className={inputCls} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="admin-review-rating-select">
                            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} Star{n > 1 ? "s" : ""}</option>)}
                        </select>
                        <input className={inputCls} placeholder="Package taken (e.g. 3 Day Pinddaan)" value={form.package_name} onChange={(e) => setForm({ ...form, package_name: e.target.value })} />
                    </div>
                    <textarea
                        className={`${inputCls} mt-3 min-h-[100px]`}
                        placeholder="What the customer said *"
                        value={form.text}
                        onChange={(e) => setForm({ ...form, text: e.target.value })}
                        data-testid="admin-review-text-input"
                    />
                    <button
                        onClick={save}
                        data-testid="admin-review-save-button"
                        className="mt-3 px-6 py-2.5 rounded-full bg-[#241D19] text-white text-xs font-semibold hover:bg-[#C85A17] transition-colors"
                    >
                        Save Review
                    </button>
                </div>
            )}

            <div className="space-y-4">
                {reviews.map((r) => (
                    <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white border border-[#E6DBCF] rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-[#241D19]">{r.name}</span>
                                <span className="inline-flex items-center gap-0.5 text-xs text-[#D4AF37]">
                                    {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={11} fill="#D4AF37" />)}
                                </span>
                                {r.location && <span className="text-xs text-[#6E6259]">{r.location}</span>}
                            </div>
                            <p className="text-xs text-[#6E6259] mt-1.5 line-clamp-2">"{r.text}"</p>
                            {r.package_name && <p className="text-[10px] text-[#B0A494] mt-1">{r.package_name}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                data-testid={`admin-review-edit-${r.id}`}
                                onClick={() => setForm({ id: r.id, name: r.name, location: r.location || "", rating: r.rating, package_name: r.package_name || "", text: r.text })}
                                className="p-2.5 rounded-lg border border-[#E6DBCF] text-[#241D19] hover:bg-[#F4EDE2]"
                                aria-label="Edit review"
                            >
                                <Pencil size={15} />
                            </button>
                            <button
                                data-testid={`admin-review-delete-${r.id}`}
                                onClick={() => remove(r.id)}
                                className="p-2.5 rounded-lg border border-[#E6DBCF] text-[#9C370B] hover:bg-red-50"
                                aria-label="Delete review"
                            >
                                <Trash2 size={15} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
